import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { Button, Collapse, Input } from 'src/components/ui';
import classnames from 'src/util/classnames';
import Icon from 'src/components/Icon';
import { makeTreeNode, organizeTreeNodes, visitTrees } from './CollapsableTreeSelect';

export type TreeNode = {
  id: number;
  labelNoDepth: string;
  path: string;
  depth: number;
  children?: TreeNode[];
  parent?: TreeNode;
};

type CheckState = { checked: boolean; indeterminate: boolean };

const READONLY_STYLE: React.CSSProperties = {
  backgroundColor: '#f5f5f5',
  color: '#616161',
  opacity: 0.7,
  cursor: 'not-allowed',
  pointerEvents: 'none',
};

function OrgUnitMultiSelect({
  options,
  selectedIds,
  onChange,
  disabled = false,
}: {
  options: any[];
  selectedIds: number[];
  onChange: (ids: number[]) => void;
  disabled?: boolean;
}) {
  const selectedSet = useMemo(() => new Set(selectedIds.map(Number)), [selectedIds]);

  const rootNodes = useMemo<TreeNode[]>(() => {
    if (!options?.length) return [];
    const nodes = options.map(ou => makeTreeNode(ou, 'id', 'name', 'parentId'));
    const roots = organizeTreeNodes(nodes);
    visitTrees(roots);
    return roots;
  }, [options]);

  const allFlatNodes = useMemo<TreeNode[]>(() => {
    const nodes: TreeNode[] = [];
    const stack = [...rootNodes];
    while (stack.length) {
      const current = stack.shift()!;
      nodes.push(current);
      if (current.children) stack.unshift(...current.children);
    }
    return nodes;
  }, [rootNodes]);

  const getAllDescendantIds = useCallback((node: TreeNode): number[] => {
    const ids: number[] = [];
    const stack = [...(node.children || [])];
    while (stack.length) {
      const curr = stack.shift()!;
      ids.push(curr.id);
      if (curr.children) stack.unshift(...curr.children);
    }
    return ids;
  }, []);

  const handleCheck = useCallback(
    (node: TreeNode, checked: boolean) => {
      const next = new Set(selectedSet);
      const allIds = [node.id, ...getAllDescendantIds(node)];
      if (checked) allIds.forEach(id => next.add(id));
      else allIds.forEach(id => next.delete(id));
      onChange(Array.from(next));
    },
    [selectedSet, getAllDescendantIds, onChange]
  );

  const getNodeCheckState = useCallback(
    (node: TreeNode): CheckState => {
      const descendantIds = getAllDescendantIds(node);
      if (!descendantIds.length) return { checked: selectedSet.has(node.id), indeterminate: false };
      const allIds = [node.id, ...descendantIds];
      const checkedCount = allIds.filter(id => selectedSet.has(id)).length;
      if (checkedCount === allIds.length) return { checked: true, indeterminate: false };
      if (checkedCount === 0) return { checked: false, indeterminate: false };
      return { checked: false, indeterminate: true };
    },
    [selectedSet, getAllDescendantIds]
  );

  const [isOpen, setOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [collapsed, _setCollapsed] = useState<Record<string, boolean>>({});
  const toggleRef = useRef<HTMLDivElement>(null);

  const setCollapsed = useCallback((path: string, val: boolean) => _setCollapsed(prev => ({ ...prev, [path]: val })), []);

  const toggle = useCallback(() => setOpen(v => !v), []);

  useEffect(() => {
    if (!isOpen) setSearchText('');
  }, [isOpen]);

  const flatSearchResults = useMemo<TreeNode[] | null>(() => {
    if (!searchText) return null;
    const lower = searchText.toLowerCase();
    return allFlatNodes.filter(n => n.labelNoDepth?.toLowerCase().includes(lower));
  }, [searchText, allFlatNodes]);

  const selectedLabels = useMemo(() => allFlatNodes.filter(n => selectedSet.has(n.id)).map(n => n.labelNoDepth), [allFlatNodes, selectedSet]);

  return (
    <div className="collapsable-tree-select input-wrapper" ref={toggleRef}>
      <div
        className={classnames('form-control tree-select-toggle', { open: isOpen, disabled })}
        onClick={disabled ? undefined : toggle}
        role="combobox"
        aria-expanded={isOpen}
        style={disabled ? READONLY_STYLE : undefined}
      >
        <span className="tree-select-value">
          {selectedLabels.length === 0 ? (
            <span className="label tree-select-placeholder">{t`Select…`}</span>
          ) : (
            <span className="label">
              {selectedLabels.slice(0, 3).join(', ')}
              {selectedLabels.length > 3 && `, +${selectedLabels.length - 3}`}
            </span>
          )}
        </span>
      </div>
      {isOpen && (
        <OrgUnitMenu toggleRef={toggleRef} onClose={() => setOpen(false)}>
          <div className="tree-select-search" onMouseDown={e => e.stopPropagation()}>
            <Input autoFocus bsSize="sm" placeholder={t`Search…`} value={searchText} onChange={e => setSearchText(e.target.value)} />
          </div>
          <div className="tree-select-items">
            {searchText ? (
              !flatSearchResults?.length ? (
                <div className="tree-select-empty">
                  <Trans>No results</Trans>
                </div>
              ) : (
                flatSearchResults.map(node => <OrgFlatNode key={node.id} node={node} getNodeCheckState={getNodeCheckState} onCheck={handleCheck} />)
              )
            ) : !rootNodes.length ? (
              <div className="tree-select-empty">
                <Trans>No options</Trans>
              </div>
            ) : (
              rootNodes.map(node => (
                <OrgTreeNode
                  key={node.id}
                  node={node}
                  collapsed={collapsed}
                  setCollapsed={setCollapsed}
                  getNodeCheckState={getNodeCheckState}
                  onCheck={handleCheck}
                />
              ))
            )}
          </div>
        </OrgUnitMenu>
      )}
    </div>
  );
}

function OrgIndeterminateCheckbox({
  checked,
  indeterminate,
  onChange,
}: {
  checked: boolean;
  indeterminate: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);
  return <input ref={ref} type="checkbox" checked={checked} onChange={onChange} className="tree-node-checkbox" onClick={e => e.stopPropagation()} />;
}

function OrgTreeNode({
  node,
  collapsed,
  setCollapsed,
  getNodeCheckState,
  onCheck,
}: {
  node: TreeNode;
  collapsed: Record<string, boolean>;
  setCollapsed: (path: string, val: boolean) => void;
  getNodeCheckState: (node: TreeNode) => CheckState;
  onCheck: (node: TreeNode, checked: boolean) => void;
}) {
  const { path, children } = node;
  const hasChildren = !!children?.length;
  const isCollapsed = collapsed?.[path];
  const { checked, indeterminate } = getNodeCheckState(node);

  const onToggle = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setCollapsed(path, !isCollapsed);
    },
    [setCollapsed, path, isCollapsed]
  );

  const onRowClick = useCallback(() => {
    if (hasChildren) setCollapsed(path, !isCollapsed);
  }, [hasChildren, setCollapsed, path, isCollapsed]);

  return (
    <>
      <div className="tree-node tree-node-selectable" style={{ paddingLeft: `${node.depth * 16 + 6}px` }} onClick={onRowClick}>
        <Button
          type="button"
          color="link"
          className="tree-node-toggle-btn"
          onClick={onToggle}
          style={{ visibility: hasChildren ? 'visible' : 'hidden' }}
          tabIndex={-1}
        >
          <Icon icon={isCollapsed ? 'Plus' : 'Minus'} weight="regular" />
        </Button>
        <OrgIndeterminateCheckbox checked={checked} indeterminate={indeterminate} onChange={e => onCheck(node, e.target.checked)} />
        <span className="tree-node-label" title={node.labelNoDepth}>
          {node.labelNoDepth}
        </span>
      </div>
      {hasChildren && (
        <Collapse isOpen={!isCollapsed}>
          {children!.map(child => (
            <OrgTreeNode
              key={child.id}
              node={child}
              collapsed={collapsed}
              setCollapsed={setCollapsed}
              getNodeCheckState={getNodeCheckState}
              onCheck={onCheck}
            />
          ))}
        </Collapse>
      )}
    </>
  );
}

function OrgFlatNode({
  node,
  getNodeCheckState,
  onCheck,
}: {
  node: TreeNode;
  getNodeCheckState: (node: TreeNode) => CheckState;
  onCheck: (node: TreeNode, checked: boolean) => void;
}) {
  const { checked, indeterminate } = getNodeCheckState(node);
  const ancestors: string[] = [];
  let cur: TreeNode | undefined = node.parent;
  while (cur) {
    ancestors.unshift(cur.labelNoDepth);
    cur = cur.parent;
  }
  return (
    <div className="tree-node tree-node-flat tree-node-selectable" onClick={() => onCheck(node, !checked)}>
      <OrgIndeterminateCheckbox checked={checked} indeterminate={indeterminate} onChange={e => onCheck(node, e.target.checked)} />
      <div className="tree-node-flat-content">
        <span className="tree-node-label" title={node.labelNoDepth}>
          {node.labelNoDepth}
        </span>
        {ancestors.length > 0 && <span className="tree-node-breadcrumb">{ancestors.join(' › ')}</span>}
      </div>
    </div>
  );
}

function OrgUnitMenu({
  toggleRef,
  onClose,
  children,
}: {
  toggleRef: React.RefObject<HTMLDivElement | null>;
  onClose: () => void;
  children: React.ReactNode;
}) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<React.CSSProperties>({ visibility: 'hidden' });

  const updatePosition = useCallback(() => {
    if (!toggleRef.current) return;
    const rect = toggleRef.current.getBoundingClientRect();
    const menuHeight = menuRef.current?.offsetHeight ?? 340;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openUpward = spaceBelow < menuHeight && spaceAbove > spaceBelow;
    setStyle({
      position: 'fixed',
      left: rect.left,
      width: rect.width,
      minWidth: Math.max(rect.width, 220),
      zIndex: 9999,
      ...(openUpward ? { bottom: window.innerHeight - rect.top, top: 'auto' } : { top: rect.bottom + 2, bottom: 'auto' }),
      visibility: 'visible',
    });
  }, [toggleRef]);

  useEffect(() => {
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [updatePosition]);

  useEffect(() => {
    function handleMouseDown(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node) && toggleRef.current && !toggleRef.current.contains(e.target as Node)) {
        onClose();
      }
    }
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [onClose, toggleRef]);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return createPortal(
    <div ref={menuRef} className="tree-select-menu" style={style}>
      {children}
    </div>,
    document.body
  );
}

export default OrgUnitMultiSelect;
